/**
 * DuplicateResidentDialog — shown by Add Resident when the name being added
 * matches an existing resident or a pending EHR move-in task
 * (docs/DUPLICATE_RESIDENT_DETECTION_PLAN.md §4).
 *
 * Opens over the Add Resident drawer, which stays open underneath, so Cancel
 * returns to the form with everything still typed. One dialog covers every case:
 *   - pending EHR move-in task: approve it on EHR Tasks if it is the same person
 *   - moved in: Add anyway
 *   - moved out: Add anyway, or Move back in (per match) into the room picked
 *     in the drawer
 *
 * A plain pp-dialog, not ConfirmTypedDialog: adding a resident is not
 * destructive, and the decision here is "which one is this", not "are you sure".
 * Cancel takes focus on open so Enter never adds a duplicate.
 *
 * The drawer behind holds no focus while this is open, so the drawer's Escape
 * handler does not fire; Escape here closes this dialog only.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MATCH_TIERS } from '../../../../utils/residentNameMatch.js';
import { utcToCampusDate } from '../../../../utils/campusTime.js';
import { hasEhrResidentId } from '../../../../services/session.js';
import './duplicate-resident-dialog.css';

const fullName = (first, last) => `${first || ''} ${last || ''}`.replace(/\s+/g, ' ').trim();

const TierTag = ({ tier }) => (
  <span className="drd-tag">{MATCH_TIERS[tier]?.label || 'Similar name'}</span>
);

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "phone, email and groups" */
const joinList = (items) => (items.length <= 1
  ? items.join('')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

const DuplicateResidentDialog = ({
  candidateName,
  areaLabel,               // the room picked in the drawer, e.g. "Room 118"
  residentMatches = [],    // [{ resident, tier }] — moved in and moved out together
  taskMatches = [],        // [{ task, tier }] — pending EHR move-in tasks
  vendorName,              // EHR vendor, for the task cards
  tzOffset = 0,
  /* (resident) => string | null. Null means Move back in is offered; a string is
     shown in its place, explaining why it is not. */
  getMoveInBlockedReason,
  /* (resident) => string[] of the typed fields Move back in will not save. */
  getDiscardedFields,
  onMoveIn,                // (resident) => void
  onAddAnyway,
  onClose,
  busy = null,             // null | 'add' | resident_id being moved in
}) => {
  const cancelRef = useRef(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);

  const movedIn = residentMatches.filter((m) => m.resident.is_active);
  const movedOut = residentMatches.filter((m) => !m.resident.is_active);
  const isBusy = busy !== null;
  const date = (v) => utcToCampusDate(v, tzOffset);

  const lead = taskMatches.length && !residentMatches.length
    ? `A move-in task for ${candidateName} is waiting on EHR Tasks.`
    : movedIn.length
      ? `A resident named ${candidateName} is already moved in.`
      : movedOut.length
        ? `${candidateName} has lived in this community before.`
        : `A resident named ${candidateName} already exists in this community.`;

  return (
    <div
      className="pp-dialog-backdrop ctd-backdrop--elevated"
      onClick={isBusy ? undefined : onClose}
    >
      <div
        className="pp-dialog pp-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drd-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !isBusy) { e.stopPropagation(); onClose(); }
        }}
      >
        <div className="pp-dialog__header">
          <h2 id="drd-title" className="pp-dialog__title">Possible duplicate resident</h2>
          <button className="dp-drawer__close" onClick={onClose} disabled={isBusy} aria-label="Close">&#x2715;</button>
        </div>

        <div className="pp-dialog__body">
          <p className="pp-dialog__text">
            {lead} Check the details below before adding.
          </p>

          {taskMatches.length ? (
            <section className="drd-section">
              <h3 className="drd-section__title">Waiting on EHR Tasks</h3>
              {taskMatches.map(({ task, tier }) => (
                <div key={`t-${task.cloud_id}`} className="drd-card drd-card--task">
                  <div className="drd-card__head">
                    <span className="drd-card__name">
                      {fullName(task.resident_first_name, task.resident_last_name)}
                      {task.resident_nickname ? <span className="drd-card__nick"> &ldquo;{task.resident_nickname}&rdquo;</span> : null}
                    </span>
                    <TierTag tier={tier} />
                  </div>
                  <p className="drd-card__meta">
                    Move-in task from {vendorName || 'the EHR'}
                    {task.area_name || task.ehr_area_name ? ` · ${task.area_name || task.ehr_area_name}` : ''}
                  </p>
                  <p className="drd-card__meta">
                    {[
                      date(task.create_time) ? `Received ${date(task.create_time)}` : '',
                      date(task.physical_move_in_date) ? `Move-in ${date(task.physical_move_in_date)}` : '',
                    ].filter(Boolean).join(' · ')}
                  </p>
                  <p className="drd-card__note">
                    If this is the same resident, approve this task instead of adding. Adding here and
                    approving the task later creates two residents.
                  </p>
                  <div className="drd-card__actions">
                    <Link to="/tasks/ehr" className="dp-btn dp-btn--secondary dp-btn--sm drd-link-btn">
                      Go to EHR Tasks
                    </Link>
                    <span className="drd-card__hint">Leaves this form without adding.</span>
                  </div>
                </div>
              ))}
            </section>
          ) : null}

          {movedIn.length ? (
            <section className="drd-section">
              <h3 className="drd-section__title">Currently moved in</h3>
              {movedIn.map(({ resident: r, tier }) => {
                /* device_count: a server match (fresh from MySQL); devices: a loaded row. */
                const devices = r.device_count != null
                  ? Number(r.device_count) || 0
                  : (Array.isArray(r.devices) ? r.devices.length : 0);
                const groups = (r.resident_groups || []).map((g) => g.resident_group_name).filter(Boolean);
                return (
                  <div key={`r-${r.resident_id}`} className="drd-card">
                    <div className="drd-card__head">
                      <span className="drd-card__name">
                        {fullName(r.resident_first_name, r.resident_last_name)}
                        {r.resident_nickname ? <span className="drd-card__nick"> &ldquo;{r.resident_nickname}&rdquo;</span> : null}
                      </span>
                      <TierTag tier={tier} />
                    </div>
                    <p className="drd-card__meta">
                      {[
                        r.area_name ? `${r.area_name}${r.area_type_name ? ` (${r.area_type_name})` : ''}` : 'No area',
                        date(r.move_in_date) ? `Moved in ${date(r.move_in_date)}` : '',
                      ].filter(Boolean).join(' · ')}
                    </p>
                    <p className="drd-card__meta">
                      {[
                        plural(devices, 'device'),
                        groups.length ? `Groups: ${groups.join(', ')}` : '',
                        hasEhrResidentId(r) ? 'EHR-linked' : '',
                      ].filter(Boolean).join(' · ')}
                    </p>
                    <div className="drd-card__actions">
                      {/* Same tab: a new tab has no session and lands on sign-in,
                          then the dashboard, never the profile. */}
                      <Link to={`/community/residents/${r.resident_id}`} className="drd-link">
                        View profile
                      </Link>
                      <span className="drd-card__hint">Leaves this form without adding.</span>
                    </div>
                  </div>
                );
              })}
            </section>
          ) : null}

          {movedOut.length ? (
            <section className="drd-section">
              <h3 className="drd-section__title">Moved out</h3>
              {movedOut.map(({ resident: r, tier }) => {
                const blocked = getMoveInBlockedReason?.(r) || null;
                const discarded = getDiscardedFields?.(r) || [];
                const movingThis = busy === r.resident_id;
                return (
                  <div key={`r-${r.resident_id}`} className="drd-card">
                    <div className="drd-card__head">
                      <span className="drd-card__name">
                        {fullName(r.resident_first_name, r.resident_last_name)}
                        {r.resident_nickname ? <span className="drd-card__nick"> &ldquo;{r.resident_nickname}&rdquo;</span> : null}
                      </span>
                      <TierTag tier={tier} />
                    </div>
                    <p className="drd-card__meta">
                      {[
                        date(r.move_in_date) ? `Moved in ${date(r.move_in_date)}` : '',
                        date(r.move_out_date) ? `Moved out ${date(r.move_out_date)}` : 'Moved out (date not recorded)',
                        hasEhrResidentId(r) ? 'EHR-linked' : '',
                      ].filter(Boolean).join(' · ')}
                    </p>
                    {blocked ? (
                      <p className="drd-card__note">
                        {blocked}{' '}
                        <Link to={`/community/residents/${r.resident_id}`} className="drd-link">
                          View profile
                        </Link>
                        <span className="drd-card__hint"> (leaves this form without adding)</span>
                      </p>
                    ) : (
                      <>
                        <p className="drd-card__note">
                          Moves the stored record back in to {areaLabel}. Devices and groups are not restored.
                          {discarded.length
                            ? ` The ${joinList(discarded)} you entered ${discarded.length === 1 ? 'is' : 'are'} not saved; update ${discarded.length === 1 ? 'it' : 'them'} on their profile.`
                            : ''}
                        </p>
                        <div className="drd-card__actions">
                          <button
                            type="button"
                            className="dp-btn dp-btn--primary dp-btn--sm"
                            onClick={() => onMoveIn(r)}
                            disabled={isBusy}
                          >
                            {movingThis ? 'Moving In…' : `Move back in to ${areaLabel}`}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </section>
          ) : null}

          <p className="drd-adding">
            You are adding: <strong>{candidateName}</strong>{areaLabel ? <> &rarr; {areaLabel}</> : null}
          </p>
        </div>

        <div className="pp-dialog__footer">
          <button ref={cancelRef} className="dp-btn dp-btn--cancel" onClick={onClose} disabled={isBusy}>
            Cancel
          </button>
          <button className="dp-btn dp-btn--primary" onClick={onAddAnyway} disabled={isBusy}>
            {busy === 'add' ? 'Adding…' : 'Add anyway'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateResidentDialog;
